'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { isEmpty } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useForm, useWatch } from 'react-hook-form'

import { Button, Form } from '@/shared/ui'

import { useResetPassword } from '../api/reset-password'
import {
  TResetPasswordForm,
  createZResetPasswordForm,
  defaultValues,
} from '../model/form'

type Props = {
  certNum: string
  onSuccess: () => void
}

export const ResetPasswordForm = ({ certNum, onSuccess }: Props) => {
  const t = useTranslations('reset_password')

  const form = useForm<TResetPasswordForm>({
    resolver: zodResolver(createZResetPasswordForm(t)),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const [newPassword, newPasswordConfirm] = useWatch({
    control: form.control,
    name: ['newPassword', 'newPasswordConfirm'],
  })

  const isDisabled = !newPassword || !newPasswordConfirm

  const resetPassword = useResetPassword({
    form,
    onSuccess,
  })

  const submitForm = ({ newPassword }: TResetPasswordForm) => {
    resetPassword.execute({ newPassword, certNum })
  }

  const handlePasswordChange = () => {
    const isPasswordMatchError =
      !!form.formState.errors.newPasswordConfirm && !isEmpty(newPasswordConfirm)

    if (isPasswordMatchError) {
      form.clearErrors('newPasswordConfirm')
    }
  }

  return (
    <Form.Root form={form}>
      <Form.Field name="newPassword" onChange={handlePasswordChange}>
        <Form.Label className="font-medium text-black">
          {t('new_password_label')}
        </Form.Label>
        <Form.PasswordField
          placeholder={t('new_password_placeholder')}
          fullWidth
        />
        <Form.Message />
      </Form.Field>

      <Form.Field name="newPasswordConfirm" className="mt-6">
        <Form.Label className="font-medium text-black">
          {t('confirm_new_password_label')}
        </Form.Label>
        <Form.PasswordField
          placeholder={t('confirm_new_password_placeholder')}
          fullWidth
        />
        <Form.Message />
      </Form.Field>

      <Button
        size="large"
        className="mt-10"
        type="submit"
        fullWidth
        onClick={form.handleSubmit(submitForm)}
        disabled={isDisabled}
      >
        {t('button.confirm')}
      </Button>
    </Form.Root>
  )
}
